/**
 * Browser RAM-only WebCrypto Cryptographic Service for Practitioner Portal.
 * 
 * Enforces Zero-Knowledge security principles:
 * 1. Private keys exist EXCLUSIVELY in volatile JavaScript memory (ArrayBuffer / CryptoKey).
 * 2. ZERO persistence to localStorage, sessionStorage, IndexedDB, or cookies.
 * 3. Envelopes are decrypted on-the-fly in browser RAM.
 */

export interface RamKeyPair {
  publicKey: CryptoKey;
  privateKey: CryptoKey;
  publicKeyPem: string;
  keyFingerprint: string;
  generatedAt: Date;
}

export interface DecryptedEnvelopeResult<T = any> {
  eventId: string;
  userId: string;
  aclMode: string;
  timestamp: number;
  data: T;
  integrityVerified: boolean;
}

export class WebCryptoRamManager {
  private activeKeyPair: RamKeyPair | null = null;

  /**
   * Generates an RSA-OAEP 2048-bit or 4096-bit key pair in volatile memory.
   */
  async generateKeyPair(modulusLength: 2048 | 4096 = 2048): Promise<RamKeyPair> {
    const keyPair = await crypto.subtle.generateKey(
      {
        name: 'RSA-OAEP',
        modulusLength,
        publicExponent: new Uint8Array([1, 0, 1]),
        hash: 'SHA-256',
      },
      true, // extractable
      ['encrypt', 'decrypt', 'wrapKey', 'unwrapKey']
    );

    const spkiBuffer = await crypto.subtle.exportKey('spki', keyPair.publicKey);
    const publicKeyPem = this.spkiToPem(spkiBuffer);
    const keyFingerprint = await this.computeFingerprint(spkiBuffer);

    this.activeKeyPair = {
      publicKey: keyPair.publicKey,
      privateKey: keyPair.privateKey,
      publicKeyPem,
      keyFingerprint,
      generatedAt: new Date(),
    };

    return this.activeKeyPair;
  }

  /**
   * Retrieves the currently active volatile key pair.
   */
  getActiveKeyPair(): RamKeyPair | null {
    return this.activeKeyPair;
  }

  /**
   * Purges volatile keys from RAM.
   */
  zeroizeKeys(): void {
    this.activeKeyPair = null;
  }

  /**
   * Computes a SHA-256 hex fingerprint formatted as SHA256:HEX
   */
  async computeFingerprint(spkiBuffer: ArrayBuffer): Promise<string> {
    const hashBuffer = await crypto.subtle.digest('SHA-256', spkiBuffer);
    const hashArray = Array.from(new Uint8Array(hashBuffer));
    const hex = hashArray.map((b) => b.toString(16).padStart(2, '0')).join('').toUpperCase();
    return `SHA256:${hex}`;
  }

  /**
   * Decrypts an EncryptedEnvelope received from the mobile app.
   */
  async decryptEnvelope<T = any>(
    envelope: {
      eventId: string;
      userId: string;
      ciphertextBase64: string;
      ivBase64: string;
      tagBase64: string;
      wrappedKeyBase64?: string | null;
      aclMode: string;
      timestamp: number;
    },
    customPrivateKey?: CryptoKey
  ): Promise<DecryptedEnvelopeResult<T>> {
    const privateKey = customPrivateKey || this.activeKeyPair?.privateKey;
    if (!privateKey) {
      throw new Error('No practitioner private key available in RAM to decrypt envelope.');
    }

    if (envelope.aclMode === 'completely_isolated') {
      return {
        eventId: envelope.eventId,
        userId: envelope.userId,
        aclMode: envelope.aclMode,
        timestamp: envelope.timestamp,
        data: { notice: 'Content is completely isolated on user device by Privacy ACL.' } as unknown as T,
        integrityVerified: true,
      };
    }

    const ciphertextBytes = this.base64ToUint8(envelope.ciphertextBase64);
    const ivBytes = this.base64ToUint8(envelope.ivBase64);
    const tagBytes = this.base64ToUint8(envelope.tagBase64);

    let decryptedBytes: ArrayBuffer;

    if (envelope.wrappedKeyBase64) {
      // 1. Unwrap AES-256-GCM symmetric key using RSA-OAEP private key
      const wrappedKeyBytes = this.base64ToUint8(envelope.wrappedKeyBase64);
      const rawAesKey = await crypto.subtle.decrypt(
        { name: 'RSA-OAEP' },
        privateKey,
        wrappedKeyBytes as any
      );

      const aesKey = await crypto.subtle.importKey(
        'raw',
        rawAesKey,
        { name: 'AES-GCM' },
        false,
        ['decrypt']
      );

      // 2. Concatenate ciphertext + 16-byte GMAC tag for WebCrypto AES-GCM
      const combinedCiphertext = new Uint8Array(ciphertextBytes.length + tagBytes.length);
      combinedCiphertext.set(ciphertextBytes, 0);
      combinedCiphertext.set(tagBytes, ciphertextBytes.length);

      decryptedBytes = await crypto.subtle.decrypt(
        {
          name: 'AES-GCM',
          iv: ivBytes as any,
          tagLength: 128,
        },
        aesKey,
        combinedCiphertext as any
      );
    } else {
      // Direct RSA-OAEP decrypt fallback for small payloads
      decryptedBytes = await crypto.subtle.decrypt(
        { name: 'RSA-OAEP' },
        privateKey,
        ciphertextBytes as any
      );
    }

    const jsonString = new TextDecoder().decode(decryptedBytes);
    let parsedData: T;
    try {
      parsedData = JSON.parse(jsonString);
    } catch {
      parsedData = jsonString as unknown as T;
    }

    return {
      eventId: envelope.eventId,
      userId: envelope.userId,
      aclMode: envelope.aclMode,
      timestamp: envelope.timestamp,
      data: parsedData,
      integrityVerified: true,
    };
  }

  /**
   * Helper: Encrypts data for testing round-trip in tests.
   */
  async encryptPayload(
    data: any,
    publicKey: CryptoKey,
    aclMode: string = 'full_metadata'
  ): Promise<{
    ciphertextBase64: string;
    ivBase64: string;
    tagBase64: string;
    wrappedKeyBase64: string;
  }> {
    const rawData = typeof data === 'string' ? data : JSON.stringify(data);
    const plaintextBytes = new TextEncoder().encode(rawData);

    // 1. Generate ephemeral AES-256-GCM symmetric key
    const aesKey = await crypto.subtle.generateKey(
      { name: 'AES-GCM', length: 256 },
      true,
      ['encrypt', 'decrypt']
    );

    // 2. Encrypt with AES-GCM
    const iv = crypto.getRandomValues(new Uint8Array(12));
    const encryptedWithTag = await crypto.subtle.encrypt(
      { name: 'AES-GCM', iv, tagLength: 128 },
      aesKey,
      plaintextBytes
    );

    const encryptedArray = new Uint8Array(encryptedWithTag);
    const ciphertext = encryptedArray.slice(0, encryptedArray.length - 16);
    const tag = encryptedArray.slice(encryptedArray.length - 16);

    // 3. Wrap AES key with RSA-OAEP public key
    const rawAesKey = await crypto.subtle.exportKey('raw', aesKey);
    const wrappedKeyBuffer = await crypto.subtle.encrypt(
      { name: 'RSA-OAEP' },
      publicKey,
      rawAesKey
    );

    return {
      ciphertextBase64: this.uint8ToBase64(ciphertext),
      ivBase64: this.uint8ToBase64(iv),
      tagBase64: this.uint8ToBase64(tag),
      wrappedKeyBase64: this.uint8ToBase64(new Uint8Array(wrappedKeyBuffer)),
    };
  }

  // --- Utility Conversions ---

  private spkiToPem(spkiBuffer: ArrayBuffer): string {
    const b64 = this.uint8ToBase64(new Uint8Array(spkiBuffer));
    const lines = b64.match(/.{1,64}/g) || [];
    return `-----BEGIN PUBLIC KEY-----\n${lines.join('\n')}\n-----END PUBLIC KEY-----`;
  }

  private uint8ToBase64(bytes: Uint8Array): string {
    if (typeof Buffer !== 'undefined') {
      return Buffer.from(bytes).toString('base64');
    }
    let binary = '';
    for (let i = 0; i < bytes.byteLength; i++) {
      binary += String.fromCharCode(bytes[i]);
    }
    return btoa(binary);
  }

  private base64ToUint8(base64: string): Uint8Array {
    if (typeof Buffer !== 'undefined') {
      return new Uint8Array(Buffer.from(base64, 'base64'));
    }
    const binary = atob(base64);
    const bytes = new Uint8Array(binary.length);
    for (let i = 0; i < binary.length; i++) {
      bytes[i] = binary.charCodeAt(i);
    }
    return bytes;
  }
}
